/**
 * FieldRenderer — Unified field component for ledger entry editor.
 * Handles TextField, Select, Checkbox, DatePicker with validation, formatting, and error display.
 */

import React, { useMemo, useState, useEffect, useLayoutEffect } from 'react';
import {
  TextField,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  Box,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Controller, UseFormReturn, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FieldKey } from '../config';
import { formatCurrency, formatPercent, formatPlainNumber, parseNumber } from '../utils/format';
import { parseLocalDate, dateToIsoLocal, formatDateDisplay } from '../utils/date';
import DateTimePickerField from './DateTimePickerField';

function normalizeLeadingZeros(raw: string): string {
  if (!raw) return '';

  let trimmed = raw.trim();
  if (trimmed === '') return '';

  trimmed = trimmed.replace(/[$€£¥%,\s]/g, '');

  let sign = '';
  let rest = trimmed;
  if (rest.startsWith('-')) {
    sign = '-';
    rest = rest.slice(1);
  } else if (rest.startsWith('+')) {
    sign = '+';
    rest = rest.slice(1);
  }

  const dotIndex = rest.indexOf('.');
  let intPart = dotIndex === -1 ? rest : rest.slice(0, dotIndex);
  const decPart = dotIndex === -1 ? '' : rest.slice(dotIndex);

  intPart = intPart.replace(/^0+/, '') || '0';

  return `${sign}${intPart}${decPart}`;
}

function countDigitsUpTo(str: string, pos: number): number {
  let digitCount = 0;
  for (let i = 0; i < pos && i < str.length; i += 1) {
    const char = str[i];
    if (/\d/.test(char)) {
      digitCount += 1;
    }
  }
  return digitCount;
}

function findPositionBeforeNextDigit(str: string, pos: number): number {
  for (let i = pos; i < str.length; i += 1) {
    if (/\d/.test(str[i])) {
      return i;
    }
  }
  return str.length;
}

function findPositionAfterDigits(str: string, digitCount: number): number {
  if (digitCount === 0) {
    for (let i = 0; i < str.length; i += 1) {
      if (/\d/.test(str[i])) {
        return i;
      }
    }
    return str.length;
  }
  let count = 0;
  for (let i = 0; i < str.length; i += 1) {
    const char = str[i];
    if (/\d/.test(char)) {
      count += 1;
      if (count === digitCount) {
        return i + 1;
      }
    }
  }
  return str.length;
}

interface FormattedTextFieldWrapperProps {
  field: any;
  fieldError?: any;
  error?: string;
  displayLabel: string;
  placeholder?: string;
  disabled?: boolean;
  helperText?: string;
  classes: any;
  inputType: string;
  required?: boolean;
  isFormattedType: boolean;
  type?: string;
  formatters: any;
}

const FormattedTextFieldWrapper: React.FC<FormattedTextFieldWrapperProps> = ({
  field,
  fieldError,
  error,
  displayLabel,
  placeholder,
  disabled,
  helperText,
  classes,
  inputType,
  required,
  isFormattedType,
  type,
  formatters,
}) => {
  const value = field.value ?? '';
  const displayValue = isFormattedType ? formatters.getDisplayValue(value) : String(value);

  const inputRef = React.useRef<HTMLInputElement>(null);
  const pendingCursorInfo = React.useRef<{ domValue: string; cursorPos: number; formatted: string } | null>(null);

  useLayoutEffect(() => {
    if (isFormattedType && pendingCursorInfo.current !== null) {
      const { domValue, cursorPos } = pendingCursorInfo.current;
      const formatted = displayValue;
      const digitCountBefore = countDigitsUpTo(domValue, cursorPos);
      const charAtCursor = domValue[cursorPos];
      const charBeforeCursor = cursorPos > 0 ? domValue[cursorPos - 1] : '';
      const isCharAtCursorDigit = charAtCursor && /\d/.test(charAtCursor);
      const isCharBeforeCursorDigit = charBeforeCursor && /\d/.test(charBeforeCursor);

      let newCursorPos: number;
      if (isCharAtCursorDigit && !isCharBeforeCursorDigit) {
        const posAfterDigits = findPositionAfterDigits(formatted, digitCountBefore);
        newCursorPos = findPositionBeforeNextDigit(formatted, posAfterDigits);
      } else {
        newCursorPos = findPositionAfterDigits(formatted, digitCountBefore);
      }

      inputRef.current?.setSelectionRange(newCursorPos, newCursorPos);
      pendingCursorInfo.current = null;
    }
  }, [displayValue, isFormattedType]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const domValue = e.target.value;
    const cursorPos = e.target.selectionStart ?? domValue.length;

    let rawValue = domValue;
    if (isFormattedType && type && ['currency', 'percent', 'number'].includes(type)) {
      rawValue = normalizeLeadingZeros(domValue);
    }

    if (isFormattedType) {
      pendingCursorInfo.current = { domValue, cursorPos, formatted: '' };
      const parsed = formatters.parse(rawValue);
      field.onChange(parsed);
    } else {
      field.onChange(rawValue);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    if (isFormattedType) {
      const parsed = formatters.parse(rawValue);
      field.onChange(parsed);
    }
    field.onBlur();
  };

  const handleFocus = () => {
    if (isFormattedType) {
      // Always use the formatter's getDisplayValue to ensure consistent formatting.
      // This prevents showing unformatted raw numbers (e.g., "1234.56" instead of "$1,234.56")
      // and prevents NaN from being displayed as the string "NaN".
      pendingCursorInfo.current = null;
    }
  };

  const isNumericType = type && ['currency', 'percent', 'number'].includes(type);
  const inputClass = isNumericType ? `${classes.input} ${classes.inputNumeric}` : classes.input;

  return (
    <Box className={classes.fieldContainer}>
      <TextField
        inputRef={inputRef}
        type={inputType}
        label={displayLabel}
        placeholder={String(placeholder ?? (type === 'date' ? 'MM/DD/YYYY' : ''))}
        disabled={disabled}
        error={Boolean(fieldError) || Boolean(error)}
        helperText={fieldError?.message || error || helperText}
        variant="outlined"
        size="small"
        fullWidth
        required={required}
        className={inputClass}
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
      />
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  fieldContainer: {
    marginBottom: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  input: {
    '& .MuiOutlinedInput-root': {
      minHeight: 40,
      height: 40,
    },
    '& .MuiOutlinedInput-input': {
      padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
      lineHeight: '1.5',
    },
  },
  select: {
    '& .MuiOutlinedInput-root': {
      minHeight: 40,
      height: 40,
    },
    '& .MuiOutlinedInput-input': {
      padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
      lineHeight: '1.5',
    },
  },
  inputNumeric: {
    '& .MuiOutlinedInput-input': {
      textAlign: 'right',
      padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
    },
  },
  label: {
    fontSize: '0.875rem',
    fontWeight: 500,
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.primary,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  },
  error: {
    fontSize: '0.75rem',
    color: theme.palette.error.main,
    marginTop: theme.spacing(0.5),
  },
  checkbox: {
    padding: theme.spacing(0.5),
  },
}));

export interface FieldRendererProps {
  fieldKey: FieldKey;
  fieldName: string;
  control: any;
  label?: string;
  type?: 'text' | 'number' | 'currency' | 'percent' | 'date' | 'select' | 'checkbox';
  options?: Array<{ value: string | number; label: string }>;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  placeholder?: string;
  helperText?: string;
  // Format options
  decimalScale?: number;
  thousandSeparator?: boolean;
  // Validation rules
  rules?: any;
}

/**
 * Get field-specific formatting and parsing for input type.
 */
function getFieldFormatters(
  type?: string,
): {
  format: (value: any) => string;
  parse: (value: string) => any;
  getDisplayValue: (value: any) => string;
} {
  switch (type) {
    case 'currency':
      return {
        format: (value) => (value === undefined || value === null ? '' : String(value)),
        parse: (value) => parseNumber(value),
        getDisplayValue: (value) => {
          const formatted = formatCurrency(value, { decimalScale: 2 });
          return formatted === '' ? '$0.00' : formatted;
        },
      };
    case 'percent':
      return {
        format: (value) => (value === undefined || value === null ? '' : String(value)),
        parse: (value) => parseNumber(value),
        getDisplayValue: (value) => formatPercent(value, 2),
      };
    case 'number':
      return {
        format: (value) => (value === undefined || value === null ? '' : String(value)),
        parse: (value) => parseNumber(value),
        getDisplayValue: (value) => formatPlainNumber(value, { decimalScale: 0 }),
      };
    case 'date':
      return {
        format: (value) => (value ? dateToIsoLocal(value) : ''),
        parse: (value) => {
          const result = parseLocalDate(value);
          return result.valid ? result.isoLocal : null;
        },
        getDisplayValue: (value) => {
          if (!value) return '';
          const result = parseLocalDate(value);
          return result.valid && result.display ? result.display : String(value);
        },
      };
    default:
      return {
        format: (value) => String(value ?? ''),
        parse: (value) => value,
        getDisplayValue: (value) => String(value ?? ''),
      };
  }
}

/**
 * Unified field renderer supporting multiple input types with formatting.
 */
export const FieldRenderer: React.FC<FieldRendererProps> = ({
  fieldKey,
  fieldName,
  control,
  label,
  type = 'text',
  options,
  required,
  disabled,
  error,
  placeholder,
  helperText,
  decimalScale = 2,
  thousandSeparator = true,
  rules,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('accounting');

  const formatters = useMemo(() => getFieldFormatters(type), [type]);

  // Validation rules combining required and custom rules
  const finalRules = useMemo(() => {
    const base = { ...rules };
    if (required) {
      base.required = t('validation:required', { defaultValue: 'This field is required' });
    }
    return base;
  }, [required, rules, t]);

  const displayLabel: string = label || t(`fields:${fieldKey}`, { defaultValue: String(fieldKey) });

  if (type === 'date') {
    return (
      <Controller
        name={fieldName}
        control={control}
        rules={finalRules}
        render={({ field, fieldState: { error: fieldError } }) => (
          <DateTimePickerField
            label={displayLabel}
            value={field.value || null}
            onChange={field.onChange}
            disabled={disabled}
            error={fieldError?.message || error}
            required={required}
          />
        )}
      />
    );
  }

  if (type === 'checkbox') {
    return (
      <Controller
        name={fieldName}
        control={control}
        rules={finalRules}
        render={({ field, fieldState: { error: fieldError } }) => (
          <Box className={classes.fieldContainer}>
            <FormControlLabel
              control={
                <Checkbox
                  {...field}
                  checked={Boolean(field.value)}
                  disabled={disabled}
                  className={classes.checkbox}
                />
              }
              label={displayLabel}
            />
            {fieldError && <Typography className={classes.error}>{fieldError.message}</Typography>}
            {error && <Typography className={classes.error}>{error}</Typography>}
          </Box>
        )}
      />
    );
  }

  if (type === 'select') {
    return (
      <Controller
        name={fieldName}
        control={control}
        rules={finalRules}
        render={({ field, fieldState: { error: fieldError } }) => {
          const currentValue = field.value === null || field.value === undefined ? '' : field.value;
          const optionsHaveValue = !!options && options.some((opt) => opt.value === currentValue);
          const needsFallback = currentValue !== '' && !optionsHaveValue;

          return (
            <Box className={classes.fieldContainer}>
              <Typography className={classes.label}>
                {displayLabel}
                {required && ' *'}
              </Typography>
              <Select
                {...field}
                value={currentValue}
                disabled={disabled}
                variant="outlined"
                className={classes.select}
              >
                <MenuItem value="">
                  <em>{placeholder || t('common:select', { defaultValue: 'Select...' })}</em>
                </MenuItem>
                {needsFallback && (
                  <MenuItem value={currentValue} key={`__fallback_${currentValue}`}>
                    {displayLabel}
                    :
                    {String(currentValue)}
                  </MenuItem>
                )}
                {options?.map((opt) => (
                  <MenuItem key={`${opt.value}`} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {fieldError && <Typography className={classes.error}>{fieldError.message}</Typography>}
              {error && <Typography className={classes.error}>{error}</Typography>}
            </Box>
          );
        }}
      />
    );
  }

  // Text, number, currency, percent fields (date handled separately above)
  const inputType = 'text';
  const isFormattedType = type && ['currency', 'percent', 'number'].includes(type);

  return (
    <Controller
      name={fieldName}
      control={control}
      rules={finalRules}
      render={({ field, fieldState: { error: fieldError } }) => (
        <FormattedTextFieldWrapper
          field={field}
          fieldError={fieldError}
          error={error}
          displayLabel={displayLabel}
          placeholder={placeholder}
          disabled={disabled}
          helperText={helperText}
          classes={classes}
          inputType={inputType}
          required={required}
          isFormattedType={isFormattedType}
          type={type}
          formatters={formatters}
        />
      )}
    />
  );
};

export default FieldRenderer;
